<template>
  <multiselect
    ref="multiSelect"
    :placeholder="$t('placeholder.select')"
    :deselect-label="deselectLabel"
    :value="value"
    :options="options"
    :track-by="trackBy"
    :label="label"
    :allow-empty="allowEmpty"
    :close-on-select="closeOnSelect"
    :open-direction="openDirection"
    :max-height="dropdownMaxHeight"
    :loading="isLoading"
    :searchable="searchable"
    :disabled="disabled"
    :show-labels="showLabel"
    select-label=""
    @input="input"
    @select="select"
    @open="open"
  >
    <template #singleLabel="{ option }">
      <slot
        :option="option"
        name="selectedLabel"
      />
    </template>

    <template #option="{ option }">
      <slot
        :option="option"
        name="option"
      />
    </template>

    <template #noResult>
      {{ $t('select.noOptionsFound') }}
    </template>

  </multiselect>
</template>

<script>
/**
 * A select component library. https://vue-multiselect.js.org/
 * This component is just a wrapper of the library to handle show keyboard on iOS.
 * Things are more encapsulated and easy to update later this way.
 */
import Multiselect from 'vue-multiselect'

export default {
  name: 'MultiSelect',
  components: { Multiselect },
  props: {
    value: {
      type: [String, Number, Object],
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    trackBy: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: [String, Number],
      default: '',
    },
    allowEmpty: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: true,
    },
    openDirection: {
      type: String,
      default: 'bottom',
    },
    dropdownMaxHeight: {
      type: Number,
      default: 300,
    },
    closeOnSelect: {
      type: Boolean,
      default: true,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    deselectLabel() {
      return this.allowEmpty ? this.$t('select.deselect') : ''
    },
  },
  methods: {
    /**
     * A hack to solve keyboard won't show problem on iOS. https://github.com/shentao/vue-multiselect/issues/717
     * @param {*} multiSelectSelector The ref of the multiSelect component.
     */
    showMultiSelectKeyboardForiOS(multiSelectSelector) {
      if (!this.isIOSDevice) {
        return
      }

      const input = multiSelectSelector.$refs.search
      if (input.style.display === 'none') {
        input.style.display = 'block'
        input.focus()
      }
    },
    open() {
      if (this.searchable) {
        this.showMultiSelectKeyboardForiOS(this.$refs.multiSelect)
      }
      this.$emit('open')
    },
    input(value) {
      this.$emit('input', value)
    },
    select(value) {
      this.$emit('select', value)
    },
  },
}
</script>

<style lang="scss">
@import '~@/assets/styles/multi-select.scss';
</style>
